import { useLocalSearchParams, useRouter } from "expo-router";
import { StyleSheet } from "react-native-unistyles";
import { Banner } from "@visioni/a3s-ui/components";
import { Text, View } from "@src/components";
import {
  INCIDENT_DEFAULT_DAYS,
  parseIncidentFilters,
  useFleetIncidents,
  type IncidentSummary,
} from "@visioni/a3s-monitoring/app";
import { EMPTY } from "../format";
import { incidentHref } from "../routes";
import { useDirectoryTenantName } from "../hooks/useDirectoryTenantName";
import { useListTimeZone } from "../hooks/useListTimeZone";
import { ClientNumberFilter, MatchedClients, NO_MATCHED_CLIENTS } from "../components/ClientNumberFilter";
import { IncidentsTable } from "../components/IncidentsTable";
import { OrderField } from "../components/OrderField";
import { QueryNotice } from "../components/QueryNotice";
import { TimeBoundFields } from "../components/TimeBoundFields";
import { TimeZoneField } from "../components/TimeZoneField";

const rawId = (id: string) => id;
const blank = (value: string | undefined) => (value === "" ? undefined : value);
// The fleet has no site, so Default is always the viewer's own zone.
const noSiteTimeZone = () => undefined;

/**
 * Every genuine incident (`?tz=&from=&to=&order=`). `?clientNo=` narrows the
 * list to the clients whose number starts with it, as on the fleet activations
 * — the incident page links here with a whole one. The fleet's totals are a
 * page of their own, with no client filter: they are counted by organisation,
 * site and camera, never by client.
 */
export function FleetIncidentsScreen() {
  const router = useRouter();
  const { tenantId } = useLocalSearchParams<{ tenantId: string }>();
  const params = useLocalSearchParams<{
    from?: string;
    to?: string;
    tz?: string;
    order?: string;
    clientNo?: string;
  }>();
  const from = blank(params.from);
  const to = blank(params.to);
  const order = blank(params.order);
  const clientNo = blank(params.clientNo);
  const { timeZone, fallback } = useListTimeZone({
    tz: blank(params.tz),
    site: undefined,
    siteTimeZone: noSiteTimeZone,
    onResolve: (tz) => router.setParams({ tz }),
  });
  const parsed =
    timeZone === undefined ? undefined : parseIncidentFilters({ from, to, tz: timeZone, order, clientNo }, new Date());
  const ok = parsed?.ok === true ? parsed : undefined;

  const list = useFleetIncidents({
    filters: ok?.filters ?? { since: "" },
    enabled: ok !== undefined,
  });

  const tenantName = useDirectoryTenantName();
  const tenantLabel = (incident: IncidentSummary) =>
    incident.a3sTenant === undefined ? EMPTY : (tenantName(incident.a3sTenant) ?? incident.a3sTenant);
  const knownTenant = (incident: IncidentSummary) =>
    incident.a3sTenant !== undefined && tenantName(incident.a3sTenant) !== undefined ? incident.a3sTenant : undefined;

  return (
    <View style={styles.page}>
      {timeZone !== undefined && (
        <View style={styles.filters}>
          <View style={styles.bounds}>
            <TimeBoundFields
              value={{ from, to }}
              onChange={(next) => router.setParams({ from: next.from, to: next.to })}
              fromPlaceholder={`Last ${String(INCIDENT_DEFAULT_DAYS)} days`}
              leading={
                <TimeZoneField
                  hasSiteFilter={false}
                  value={timeZone}
                  fallback={fallback}
                  at={ok !== undefined ? new Date(ok.filters.since) : new Date()}
                  onChange={(tz) => router.setParams({ tz })}
                />
              }
            />
          </View>
          <View style={styles.row}>
            <View style={styles.field}>
              <OrderField value={order} onChange={(next) => router.setParams({ order: next })} />
            </View>
            <ClientNumberFilter
              clientNo={clientNo}
              onChange={(next) => router.setParams({ clientNo: next })}
              rows="genuine incidents"
            />
          </View>
          <MatchedClients rows="Genuine incidents" clientNos={list.clientNos} />
        </View>
      )}
      {parsed === undefined || timeZone === undefined ?
        <View style={styles.notice}>
          <QueryNotice isLoading error={null} onRetry={list.refetch} />
        </View>
      : !parsed.ok ?
        <View style={styles.notice}>
          <Banner variant="warning" title="Filters not applied" message={parsed.problem} />
        </View>
      : list.items === undefined || list.error !== null ?
        <View style={styles.notice}>
          <QueryNotice isLoading={list.isLoading} error={list.error} onRetry={list.refetch} />
        </View>
      : list.clientNos?.length === 0 ?
        <View style={styles.centered}>
          <Text textStyle="sm/regular" color="textMuted">
            {NO_MATCHED_CLIENTS}
          </Text>
        </View>
      : list.items.length === 0 && list.nextCursor === null && !list.pager.canGoBack ?
        <View style={styles.centered}>
          <Text textStyle="sm/regular" color="textMuted">
            No genuine incidents found
          </Text>
        </View>
      : <IncidentsTable
          items={list.items}
          isStale={list.isPlaceholderData}
          isFetching={list.isFetching}
          hrefFor={(incident) => incidentHref(tenantId, incident.id, knownTenant(incident))}
          tenantLabel={tenantLabel}
          siteLabel={rawId}
          cameraLabel={rawId}
          timeZone={timeZone}
          pager={list.pager}
          nextCursor={list.nextCursor}
        />
      }
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  page: { flex: 1 },
  filters: {
    gap: theme.spacing.lg,
    paddingHorizontal: theme.spacing.xl,
    paddingVertical: theme.spacing.xl,
    // Above the list, so the pickers open over the rows.
    zIndex: 1,
  },
  // Above the fields below it, so the timezone list and calendars open over them.
  bounds: { zIndex: 1 },
  row: { flexDirection: "row", flexWrap: "wrap", alignItems: "flex-start", gap: theme.spacing.lg },
  // The width of the timezone field above it.
  field: { flexBasis: "24%", maxWidth: 420, minWidth: 220 },
  notice: { paddingHorizontal: theme.spacing.xl },
  centered: { flex: 1, alignItems: "center", justifyContent: "center", padding: theme.spacing["4xl"] },
}));
